import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Calendar, MessageSquare, Send, Check, Loader2 } from 'lucide-react';
import { PublicHeader } from '@/components/common/PublicHeader';
import { PublicFooter } from '@/components/common/PublicFooter';
import api from '@/services/api';
import { toast } from 'sonner';

/**
 * ContactPage — single public entry-point for two intents:
 *   /contact           → generic contact form (subject defaults to "General inquiry")
 *   /contact?type=demo → "Book a Demo" variant (subject/copy switch to demo booking language)
 *
 * Backed by POST /api/contact/submit — admins see submissions in
 * Admin → Support → Contact submissions.
 */
export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const isDemo = searchParams.get('type') === 'demo';

  const [form, setForm] = useState({ name: '', email: '', company: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setForm((f) => ({
      ...f,
      subject: isDemo ? 'Book a demo — Harmonix' : (f.subject || ''),
      message: isDemo && !f.message
        ? "Hi Harmonix team,\n\nI'd like to see a live demo of your AI Employee for my business.\n\nBusiness type: \nTeam size: \nBest time to call: "
        : f.message,
    }));
  }, [isDemo]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error('Please fill in name, email, subject and message.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/contact/submit', form);
      setSubmitted(true);
      toast.success(isDemo ? 'Demo request received — check your inbox soon!' : 'Message sent!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{isDemo ? 'Book a Demo | Harmonix' : 'Contact Us | Harmonix'}</title>
        <meta
          name="description"
          content={isDemo
            ? 'Book a personalised live demo of the Harmonix AI Employee. See it answer real customer questions and book appointments in real time.'
            : 'Get in touch with the Harmonix team — questions, partnerships, or press.'}
        />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-white to-teal-50/30" data-testid="contact-page">
        <PublicHeader />

        <section className="max-w-3xl mx-auto px-4 sm:px-6 pt-20 pb-16">
          <div className="text-center mb-10">
            <Badge className={isDemo ? 'bg-teal-50 text-teal-700 border-teal-200 mb-4' : 'bg-slate-100 text-slate-700 border-slate-200 mb-4'}>
              {isDemo ? <Calendar className="w-3 h-3 mr-1" /> : <MessageSquare className="w-3 h-3 mr-1" />}
              {isDemo ? 'Book a Demo' : 'Contact us'}
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-4 leading-tight">
              {isDemo ? 'See Harmonix in action' : 'Talk to a human'}
            </h1>
            <p className="text-lg text-slate-600 max-w-xl mx-auto">
              {isDemo
                ? 'Get a 20-minute walkthrough of Harmonix, customised to your industry. We\'ll show your AI Employee live before you commit.'
                : 'Questions, partnerships, press — send us a note and we\'ll reply within 24 hours.'}
            </p>
          </div>

          {submitted ? (
            <Card className="border-emerald-200 bg-emerald-50/50" data-testid="contact-success">
              <CardContent className="p-8 text-center">
                <div className="w-14 h-14 rounded-full bg-emerald-100 mx-auto mb-4 flex items-center justify-center">
                  <Check className="w-7 h-7 text-emerald-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">
                  {isDemo ? "You're on the list!" : "Message sent!"}
                </h2>
                <p className="text-slate-600 max-w-md mx-auto">
                  {isDemo
                    ? "We'll email you within one business day to lock a demo slot that works for your timezone."
                    : "We\u2019ll be in touch within 24 hours. In the meantime, feel free to explore our docs or try the free plan."}
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-6 sm:p-8">
                <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="c-name" className="text-slate-700">Name</Label>
                      <Input id="c-name" value={form.name} onChange={(e) => setField('name', e.target.value)} placeholder="Jamie Rivera" data-testid="contact-name" />
                    </div>
                    <div>
                      <Label htmlFor="c-email" className="text-slate-700">Work email</Label>
                      <Input id="c-email" type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} placeholder="you@company.com" data-testid="contact-email" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="c-company" className="text-slate-700">Company <span className="text-slate-400 text-xs">(optional)</span></Label>
                    <Input id="c-company" value={form.company} onChange={(e) => setField('company', e.target.value)} placeholder="Acme Inc." data-testid="contact-company" />
                  </div>
                  <div>
                    <Label htmlFor="c-subject" className="text-slate-700">Subject</Label>
                    <Input id="c-subject" value={form.subject} onChange={(e) => setField('subject', e.target.value)} placeholder={isDemo ? 'Book a demo — Harmonix' : 'How can we help?'} data-testid="contact-subject" />
                  </div>
                  <div>
                    <Label htmlFor="c-message" className="text-slate-700">Message</Label>
                    <Textarea
                      id="c-message"
                      value={form.message}
                      onChange={(e) => setField('message', e.target.value)}
                      rows={7}
                      placeholder={isDemo ? "Tell us about your business, team size, and what you'd like the demo to focus on." : 'Tell us what you need…'}
                      data-testid="contact-message"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-teal-600 hover:bg-teal-700 text-white h-12 text-base font-semibold"
                    data-testid="contact-submit-btn"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    {isDemo ? 'Request my demo' : 'Send message'}
                  </Button>
                  <p className="text-xs text-slate-500 text-center">
                    By submitting you agree to our <a href="/legal/privacy" className="underline">Privacy Policy</a>. No spam — we reply within 24 hours.
                  </p>
                </form>
              </CardContent>
            </Card>
          )}
        </section>

        <PublicFooter />
      </div>
    </>
  );
}
